import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { formatDateTimeLong } from '../utils/formatDate';
import { resolveApiBaseUrl } from '../utils/apiBase';
import { usePageMeta } from '../hooks/usePageMeta';
import { PageError, PageLoading, PageEmpty } from '../components/PageStatus';
import { TIME_SLOTS, nextReportRunLabel, resolvePageMode } from './newsPageMode';
import {
  LIVE_POLL_INTERVAL_MS,
  activeEditionNeedsRefresh,
  fetchEditionSummaries,
  sortReportTabs,
  summarizeLastSeen,
  summariesByEditionMap,
  tabFreshnessMeta,
} from './newsPageEditions';
import { resolveSourceNavigation } from './newsPageSourceArticle';
import styles from './NewsPage.module.css';

const NewsPage = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const pageMode = useMemo(() => resolvePageMode(section), [section]);
  const isHourlyTab = pageMode.slug === 'hourly';
  const isReport = pageMode.mode === 'report';
  const isTopic = pageMode.mode === 'topic';
  const showLiveAside = isReport || isTopic;

  useEffect(() => {
    if (section === 'frontpage') {
      navigate('/news', { replace: true });
    } else if (pageMode.mode === 'unknown') {
      navigate('/news', { replace: true });
    }
  }, [section, pageMode.mode, navigate]);

  const pageTitle = useMemo(() => {
    if (isHourlyTab) return 'News';
    if (isReport || isTopic) return `${pageMode.label} · News`;
    return 'News';
  }, [isHourlyTab, isReport, isTopic, pageMode.label]);

  usePageMeta({
    title: pageTitle,
    path: pageMode.path || '/news',
    description: isHourlyTab
      ? 'DarkNews Hourly digest — last three hours across sectors.'
      : isReport
        ? `${pageMode.label} timed report on DarkNews — previous three hours of coverage.`
        : `${pageMode.label} digest on DarkNews — composed from independent RSS sources.`,
  });

  const [newsData, setNewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(true);
  const [editionSummaries, setEditionSummaries] = useState([]);
  const [selectedArticleId, setSelectedArticleId] = useState(null);
  const [paneArticle, setPaneArticle] = useState(null);
  const [paneLoading, setPaneLoading] = useState(false);
  const [paneError, setPaneError] = useState(null);
  const [paneReloadToken, setPaneReloadToken] = useState(0);
  const [livePaused, setLivePaused] = useState(
    () => typeof window !== 'undefined'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const lastSeenGeneratedAtRef = useRef({});

  const clearArticlePane = useCallback(() => {
    setSelectedArticleId(null);
    setPaneArticle(null);
    setPaneError(null);
    setPaneLoading(false);
  }, []);

  useEffect(() => {
    clearArticlePane();
  }, [pageMode.slug, clearArticlePane]);

  useEffect(() => {
    if (!selectedArticleId) {
      setPaneArticle(null);
      setPaneError(null);
      setPaneLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadArticle() {
      setPaneLoading(true);
      setPaneError(null);
      try {
        const response = await fetch(`${resolveApiBaseUrl()}/feed/${selectedArticleId}`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? 'Article not found in archive'
              : `Failed to load article (${response.status})`,
          );
        }
        const data = await response.json();
        if (!cancelled) {
          setPaneArticle(data);
        }
      } catch (err) {
        if (!cancelled) {
          setPaneArticle(null);
          setPaneError(err.message || 'Failed to load article');
        }
      } finally {
        if (!cancelled) {
          setPaneLoading(false);
        }
      }
    }
    loadArticle();
    return () => {
      cancelled = true;
    };
  }, [selectedArticleId, paneReloadToken]);

  const handleSourceActivate = useCallback((event, source) => {
    const nav = resolveSourceNavigation(source);
    if (nav.mode !== 'in-page') {
      return;
    }
    event.preventDefault();
    setSelectedArticleId(nav.articleId);
  }, []);

  const retryPaneArticle = useCallback(() => {
    setPaneReloadToken((token) => token + 1);
  }, []);

  const fetchEdition = useCallback(async (editionSlug) => {
    const response = await fetch(`${resolveApiBaseUrl()}/news/editions/${editionSlug}`, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404) {
      setNewsData(null);
      setPending(true);
      setError(null);
      return;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    setNewsData(data);
    setPending(false);
    setError(null);
    if (data.generatedAt) {
      lastSeenGeneratedAtRef.current = {
        ...lastSeenGeneratedAtRef.current,
        [editionSlug]: data.generatedAt,
      };
    }
  }, []);

  const refreshEditionMetadata = useCallback(async ({ refreshActiveEdition = false } = {}) => {
    const summaries = await fetchEditionSummaries(resolveApiBaseUrl());
    setEditionSummaries(summaries);

    if (
      refreshActiveEdition
      && (isReport || isTopic)
      && activeEditionNeedsRefresh(summaries, lastSeenGeneratedAtRef.current, pageMode.slug)
    ) {
      await fetchEdition(pageMode.slug);
    }

    lastSeenGeneratedAtRef.current = summarizeLastSeen(summaries);
    return summaries;
  }, [fetchEdition, isReport, isTopic, pageMode.slug]);

  useEffect(() => {
    if (pageMode.mode === 'unknown') {
      setLoading(false);
      setError(null);
      setNewsData(null);
      setPending(false);
      return undefined;
    }

    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setError(null);
        setPending(false);
        const tasks = [refreshEditionMetadata()];
        if (isTopic || isReport) {
          tasks.push(fetchEdition(pageMode.slug));
        }
        await Promise.all(tasks);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching news:', err);
          setError(`Failed to load news: ${err.message}. Please try again later.`);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [isReport, isTopic, pageMode.mode, pageMode.slug, fetchEdition, refreshEditionMetadata]);

  useEffect(() => {
    if (livePaused) return undefined;

    const tick = () => {
      if (document.hidden) return;
      refreshEditionMetadata({ refreshActiveEdition: true }).catch((err) => {
        console.error('Edition metadata poll failed:', err);
      });
    };

    const intervalId = window.setInterval(tick, LIVE_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [livePaused, refreshEditionMetadata]);

  const retry = () => {
    setLoading(true);
    setPending(false);
    Promise.all([
      refreshEditionMetadata(),
      isTopic || isReport ? fetchEdition(pageMode.slug) : Promise.resolve(),
    ])
      .catch((err) => {
        console.error('Error retrying news load:', err);
        setError(`Failed to load news: ${err.message}. Please try again later.`);
      })
      .finally(() => setLoading(false));
  };

  const sortedTabs = useMemo(() => {
    if (!editionSummaries.length) return TIME_SLOTS;
    return sortReportTabs(TIME_SLOTS, summariesByEditionMap(editionSummaries));
  }, [editionSummaries]);

  const sectionNav = (
    <nav className={styles.sectionNav} aria-label="News editions">
      {sortedTabs.map((item) => {
        const summary = editionSummaries.find((entry) => entry.edition === item.slug);
        const freshness = tabFreshnessMeta(summary);
        const isActive = item.slug === pageMode.slug;
        const tabClassNames = [
          styles.sectionTab,
          isActive ? styles.sectionTabActive : '',
          !isActive && freshness.state === 'fresh' ? styles.sectionTabFresh : '',
          !isActive && freshness.state === 'stale' ? styles.sectionTabStale : '',
          !isActive && freshness.state === 'pending' ? styles.sectionTabPending : '',
        ].filter(Boolean).join(' ');

        const tabStyle = !isActive && freshness.state === 'fresh'
          ? { '--tab-freshness': freshness.freshness }
          : undefined;

        return (
          <Link
            key={item.slug}
            to={item.path}
            className={tabClassNames}
            style={tabStyle}
            aria-current={isActive ? 'page' : undefined}
          >
            <span className={styles.tabLabelFull}>{item.label}</span>
            <span className={styles.tabLabelShort}>{item.shortLabel || item.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  const pageHeader = ({ kicker, title, lead, timestamp, stale, hourlyBack, articleBack }) => (
    <header className={`reader-header reader-header--split ${styles.pageHeader}`}>
      <div className="reader-header-main">
        {articleBack && (
          <p className={styles.headerNav}>
            <button
              type="button"
              className={styles.headerBackButton}
              onClick={clearArticlePane}
            >
              ← Back to edition
            </button>
          </p>
        )}
        {!articleBack && hourlyBack && (
          <p className={styles.headerNav}>
            <Link to="/news" className={styles.headerBackLink}>← Hourly overview</Link>
          </p>
        )}
        <p className="reader-kicker">{kicker}</p>
        <h1>
          {title}
          {stale ? <span className={styles.staleBadge}> Stale</span> : null}
        </h1>
        {lead && <p className="reader-lead">{lead}</p>}
      </div>
      {showLiveAside ? (
        <aside className={styles.headerAside}>
          {timestamp && (
            <time className={styles.editionDate} dateTime={timestamp}>
              {formatDateTimeLong(timestamp)}
            </time>
          )}
          <button
            type="button"
            className={`${styles.liveBtn}${livePaused ? '' : ` ${styles.liveBtnActive}`}`}
            onClick={() => setLivePaused((paused) => !paused)}
            title={livePaused ? 'Resume live refresh' : 'Pause live refresh'}
            aria-pressed={!livePaused}
          >
            <span
              className={`${styles.liveDot}${livePaused ? '' : ` ${styles.liveDotPulse}`}`}
              aria-hidden="true"
            />
            {livePaused ? 'Paused' : 'LIVE'}
          </button>
        </aside>
      ) : (
        timestamp && (
          <time className={styles.editionDate} dateTime={timestamp}>
            {formatDateTimeLong(timestamp)}
          </time>
        )
      )}
    </header>
  );

  const pendingView = () => {
    const nextRun = nextReportRunLabel(pageMode.slug);
    return (
      <>
        {pageHeader({
          kicker: isHourlyTab ? 'Hourly digest' : 'Timed report',
          title: pageMode.label,
          lead: 'Cached digest for this Eastern-time slot — no live LLM on page load.',
          hourlyBack: !isHourlyTab,
        })}
        <div className={`reader-card ${styles.placeholderCard}`}>
          <PageEmpty
            title={`${pageMode.label} — edition pending`}
            message={
              nextRun
                ? `The scheduled digest has not run yet. Next run ${nextRun}.`
                : 'The scheduled digest has not run yet. Check back soon.'
            }
          >
            {!isHourlyTab && (
              <Link to="/news" className="reader-btn reader-btn-secondary">
                View Hourly edition
              </Link>
            )}
          </PageEmpty>
        </div>
      </>
    );
  };

  const editionDigest = ({ kicker, lead }) => {
    const sources = newsData?.sources ?? [];
    const sourceCount = newsData?.sourceCount ?? sources.length;
    const viewingArticle = Boolean(selectedArticleId);
    const headerTitle = viewingArticle && paneArticle?.title
      ? paneArticle.title
      : pageMode.label;
    const headerLead = viewingArticle
      ? (paneArticle?.source
        ? `${paneArticle.source}${paneArticle.publishedAt ? ` · ${formatDateTimeLong(paneArticle.publishedAt)}` : ''}`
        : lead)
      : lead;

    return (
      <div className={styles.editionDigest}>
        {pageHeader({
          kicker: viewingArticle ? 'Source article' : kicker,
          title: headerTitle,
          lead: headerLead,
          timestamp: viewingArticle ? undefined : newsData?.generatedAt,
          stale: viewingArticle ? false : newsData?.stale,
          hourlyBack: !isHourlyTab,
          articleBack: viewingArticle,
        })}

        <div
          className={[
            styles.editionLayout,
            sources.length > 0 ? styles.editionLayoutHasSources : '',
            sourcesOpen ? styles.editionLayoutSourcesOpen : '',
          ].filter(Boolean).join(' ')}
        >
          <div className={styles.editionMain}>
            <div className={`reader-card ${styles.mainArticle}`}>
              {viewingArticle ? (
                <div className={styles.articlePane}>
                  {paneLoading && <PageLoading message="Loading article…" />}
                  {!paneLoading && paneError && (
                    <PageError
                      title="Couldn’t load article"
                      message={paneError}
                      onRetry={retryPaneArticle}
                    />
                  )}
                  {!paneLoading && !paneError && paneArticle && (
                    <>
                      <div className={styles.articlePaneActions}>
                        {paneArticle.url && (
                          <a
                            href={paneArticle.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.articlePaneLink}
                          >
                            Original
                          </a>
                        )}
                      </div>
                      {paneArticle.summary?.trim() ? (
                        <div
                          className={`${styles.body} ${styles.articlePaneBody}`}
                          dangerouslySetInnerHTML={{ __html: paneArticle.summary }}
                        />
                      ) : (
                        <p className={styles.articlePaneEmpty}>
                          No archived summary for this article.
                          {paneArticle.url ? (
                            <>
                              {' '}
                              <a href={paneArticle.url} target="_blank" rel="noopener noreferrer">
                                Open original
                              </a>
                            </>
                          ) : null}
                        </p>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className={styles.body}>
                  <ReactMarkdown>{newsData.content}</ReactMarkdown>
                </div>
              )}
            </div>
          </div>

          {sources.length > 0 && (
            <aside
              className={`${styles.sourcesDrawer}${sourcesOpen ? ` ${styles.sourcesDrawerOpen}` : ''}`}
              aria-label="Edition sources"
            >
              <button
                type="button"
                className={styles.sourcesRail}
                onClick={() => setSourcesOpen((open) => !open)}
                aria-expanded={sourcesOpen}
                title={sourcesOpen ? 'Collapse sources' : 'Expand sources'}
              >
                <span className={styles.sourcesRailLabel}>SOURCES</span>
                <span className={styles.sourcesCount}>{sourceCount}</span>
                <span className={styles.sourcesRailCaret} aria-hidden="true">
                  {sourcesOpen ? '▸' : '◂'}
                </span>
              </button>
              <div className={styles.sourcesPanel}>
                <header className={styles.sourcesPanelHeader}>
                  <h2>Sources</h2>
                  <p>{sourceCount} articles in this edition&apos;s retrieval window</p>
                </header>
                <ol className={styles.sourcesList}>
                  {sources.map((source, index) => {
                    const nav = resolveSourceNavigation(source);
                    const isSelected = nav.mode === 'in-page'
                      && nav.articleId === selectedArticleId;
                    return (
                      <li
                        key={source.articleId || source.url || `${source.title}-${index}`}
                        className={isSelected ? styles.sourceItemSelected : undefined}
                      >
                        {nav.mode === 'in-page' ? (
                          <button
                            type="button"
                            className={styles.sourceTitleBtn}
                            onClick={(event) => handleSourceActivate(event, source)}
                            aria-current={isSelected ? 'true' : undefined}
                          >
                            {source.title}
                          </button>
                        ) : (
                          <a href={source.url} target="_blank" rel="noopener noreferrer">
                            {source.title}
                          </a>
                        )}
                        {source.source ? (
                          <span className={styles.sourcePublisher}>{source.source}</span>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              </div>
            </aside>
          )}
        </div>
      </div>
    );
  };

  const shell = (content) => (
    <div className={`reader-page ${styles.newsPage}`}>
      <div className="container">
        {sectionNav}
        <div className={styles.newsMain}>{content}</div>
      </div>
    </div>
  );

  if (loading) {
    return shell(<PageLoading message={`Loading ${pageMode.label?.toLowerCase() || 'news'}…`} />);
  }

  if (error) {
    return shell(<PageError title={pageMode.label || 'News'} message={error} onRetry={retry} />);
  }

  if (pending && isReport) {
    return shell(pendingView());
  }

  if ((isTopic || isReport) && (!newsData || (isTopic && newsData.sourceCount === 0))) {
    return shell(
      <div className={`reader-card ${styles.placeholderCard}`}>
        <PageEmpty
          title={newsData?.title || 'No news available'}
          message={newsData?.content || 'No recent articles found. Please check back later.'}
        />
      </div>,
    );
  }

  if (isReport && newsData) {
    return shell(
      editionDigest({
        kicker: isHourlyTab ? 'Hourly digest' : 'Timed report',
        lead: 'Previous three hours of coverage — Eastern-time slot.',
      }),
    );
  }

  if (isTopic && newsData) {
    return shell(
      editionDigest({
        kicker: 'Section digest',
        lead: 'Full markdown edition for this topic.',
      }),
    );
  }

  return null;
};

export default NewsPage;

export { TIME_SLOTS, TOPIC_SECTIONS, resolvePageMode } from './newsPageMode';
